import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import fr from "./fr.json";

/**
 * Locale bundles are ~110 kB of raw JSON EACH. Bundling all three shipped
 * ~340 kB in the startup chunk to render the interface in ONE language.
 *
 * French is bundled statically because it is both the default and the
 * `fallbackLng` — it must always be present synchronously, or a missing key in
 * another locale would render as a raw key path. English and Arabic are fetched
 * on demand: at startup by `main.tsx` when one of them is the persisted
 * language (while the static shell from index.html is on screen, so nothing
 * flashes in the wrong language), and on switch by the UI store.
 */

export type AppLanguage = "fr" | "en" | "ar";

const LANG_KEY = "fad.lang";

const LOADERS: Record<Exclude<AppLanguage, "fr">, () => Promise<{ default: object }>> = {
  en: () => import("./en.json"),
  ar: () => import("./ar.json"),
};

/** The persisted language, or French. Safe when storage is unavailable (Tauri, private mode). */
export function storedLanguage(): AppLanguage {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === "en" || v === "ar" || v === "fr") return v;
  } catch {
    // localStorage unavailable — fall through to the default.
  }
  return "fr";
}

/**
 * Make sure a locale's messages are loaded. Resolves immediately for French and
 * for anything already loaded; never rejects — a failed locale fetch must
 * degrade to the French fallback, not break startup.
 */
export async function ensureLanguage(lang: AppLanguage): Promise<void> {
  if (lang === "fr" || i18n.hasResourceBundle(lang, "translation")) return;
  try {
    const mod = await LOADERS[lang]();
    i18n.addResourceBundle(lang, "translation", mod.default, true, true);
  } catch {
    // Chunk unreachable (offline / stale deploy): keep the French fallback.
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    fr: { translation: fr },
  },
  lng: storedLanguage(),
  fallbackLng: "fr",
  interpolation: { escapeValue: false },
});

export default i18n;
